/**
 * LocationSection Component
 *
 * Settings section for live location sharing. Renders a single toggle that
 * shares the user's live position with workspace members. Enabling it requires
 * geolocation consent, which MUST be requested from the toggle's user gesture —
 * we never persist an enabled-but-denied state.
 *
 * @module pages/SettingsPage/components/LocationSection
 */

import { type ReactElement, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useGeoMarkupStore } from '@/store';
import { NotificationService } from '@/services/notificationService';
import { SettingsToggleRow } from '@/components/common/SettingsToggleRow';
import { requestLocationConsent } from '@/utils/geolocation/requestLocationConsent';

import styles from './LocationSection.module.css';

export const LocationSection = memo(function LocationSection(): ReactElement {
  const { t } = useTranslation();

  const shareLocation = useGeoMarkupStore((state) => state.map.shareLocation);
  const setShareLocation = useGeoMarkupStore(
    (state) => state.map.setShareLocation
  );

  const handleShareLocationChange = useCallback(
    async (next: boolean) => {
      if (!next) {
        setShareLocation(false);
        return;
      }

      try {
        const granted = await requestLocationConsent();
        if (granted) {
          setShareLocation(true);
        } else {
          // Keep sharing OFF — never persist enabled-but-denied.
          NotificationService.showError(
            t('settings.location.permissionDenied')
          );
        }
      } catch (error: unknown) {
        console.error('Failed to request location consent:', error);
        NotificationService.showError(t('settings.location.permissionDenied'));
      }
    },
    [setShareLocation, t]
  );

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>{t('settings.location.title')}</h2>
      <hr className={styles.divider} />

      <SettingsToggleRow
        label={t('settings.location.shareLabel')}
        description={t('settings.location.shareDescription')}
        checked={shareLocation}
        onChange={(next) => void handleShareLocationChange(next)}
      />
    </div>
  );
});
